import React, {
  ReactNode,
  RefObject,
  createContext,
  useContext,
  useEffect,
  useReducer,
} from "react";
import Loading from "../../components/loading";

type Action = {
  type: "set_ref" | "start_loading" | "stop_loading" | "set_message";
  ref?: RefObject<ReactNode>;
  message?: string;
};
type State = {
  isLoading: boolean;
  message?: string | React.ReactNode;
  ref?: RefObject<ReactNode>;
};
type Context = {
  state: State;
  setRef: (ref: RefObject<ReactNode>) => void;
  setMessage: (message: string) => void;
  startLoading: () => void;
  stopLoading: () => void;
};
type LoadingProviderProps = { children: React.ReactNode };
type UseLoadingProps = {
  ref?: RefObject<ReactNode>;
  message?: string;
};

const LoadingContext = createContext<Context | undefined>(undefined);

function loadingReducer(state: State, action: Action) {
  switch (action.type) {
    case "set_ref": {
      return { ...state, ref: action.ref };
    }
    case "start_loading": {
      return { ...state, isLoading: true };
    }
    case "stop_loading": {
      return { ...state, isLoading: false };
    }
    case "set_message": {
      return { ...state, message: action.message };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function LoadingProvider({ children }: LoadingProviderProps) {
  const [{ isLoading, message, ref }, dispatch] = useReducer(loadingReducer, {
    isLoading: false,
    message: "Loading",
    ref: undefined,
  });

  const setRef = (ref: RefObject<ReactNode>) => {
    dispatch({ type: "set_ref", ref });
  };

  const setMessage = (message: string) => {
    dispatch({ type: "set_message", message });
  };

  const startLoading = () => {
    dispatch({ type: "start_loading" });
  };

  const stopLoading = () => {
    dispatch({ type: "stop_loading" });
  };

  return (
    <LoadingContext.Provider
      value={{
        state: { ref, message, isLoading },
        setMessage,
        setRef,
        startLoading,
        stopLoading,
      }}
    >
      {!ref && <Loading>{message}</Loading>}
      {children}
    </LoadingContext.Provider>
  );
}

function useLoading({ ref, message }: UseLoadingProps = {}) {
  const context = useContext(LoadingContext);

  // only happens if there is an initial mode value.
  useEffect(() => {
    ref && context?.setRef(ref);
    message && context?.setMessage(message);
  }, []);

  if (context === undefined) {
    throw new Error("useLoading must be used within a LoadingProvider");
  }

  return context;
}

export { LoadingContext, LoadingProvider, useLoading };
